/**
 * Theme Applicator - Syncs store theme state to document classes and styles
 *
 * Applies the following classes to <html>:
 * - 'dark' when dark mode is active
 * - Theme classes: theme-soft, theme-brutal, theme-neon, theme-pastel,
 *   theme-jeju, theme-mono, theme-forest, theme-deepgreen
 *
 * For custom themes (designStyle starts with 'custom-'):
 * - Applies CSS variables inline to <html> element
 * - Removes built-in theme classes
 *
 * @module core/foundation/ThemeApplicator
 */

import { useEffect } from "react";
import { useGlobalStore } from "~/core/store/useGlobalStore";
import { getThemeClassName } from "~/core/config/themes";
import { ensureReadableOn, hexToHsl, hexToRgb } from "~/core/utils/colorUtils";
import type { ThemePalette } from "~/core/utils/colorUtils";
import type { BuiltInDesignStyle } from "~/core/store/useGlobalStore";

/**
 * All possible theme classes that can be applied
 */
const THEME_CLASSES = [
  "theme-soft",
  "theme-brutal",
  "brutal",          // Legacy alias
  "theme-neon",
  "theme-pastel",
  "theme-jeju",
  "theme-mono",
  "theme-forest",
  "theme-deepgreen",
];

/**
 * CSS variable keys that custom themes can override
 */
const CUSTOM_THEME_CSS_VARS: (keyof ThemePalette)[] = [
  '--bg-main',
  '--bg-card',
  '--bg-elevated',
  '--text-main',
  '--text-muted',
  '--text-subtle',
  '--border-color',
  '--border-subtle',
  '--accent-color',
  '--accent-color-hover',
  '--secondary-color',
  '--secondary-color-hover',
  '--finder-bg',
  '--finder-item-hover',
  '--dock-bg',
  '--status-accent-bg',
  '--status-accent-bg-subtle',
  '--status-accent-text',
  '--status-accent-icon',
];

/**
 * Color variables that should have RGB variants generated
 */
const COLOR_VARS_WITH_RGB = [
  '--accent-color',
  '--secondary-color',
];

/**
 * Variables that drive `text-accent`/`bg-accent` etc. — must keep
 * readable contrast against `--bg-card`. Clamped at apply time so
 * legacy palettes saved before ensureReadableOn was introduced still
 * render legibly.
 */
const ACCENT_LIKE_VARS: (keyof ThemePalette)[] = [
  '--accent-color',
  '--accent-color-hover',
  '--secondary-color',
  '--secondary-color-hover',
];

/**
 * Apply custom theme CSS variables to root element
 */
function applyCustomThemeVariables(root: HTMLElement, palette: ThemePalette) {
  let bgCardL: number | null = null;
  try {
    bgCardL = hexToHsl(palette['--bg-card']).l;
  } catch {
    bgCardL = null;
  }

  CUSTOM_THEME_CSS_VARS.forEach((varName) => {
    const rawValue = palette[varName];
    if (!rawValue) return;

    const value = bgCardL !== null && (ACCENT_LIKE_VARS as string[]).includes(varName)
      ? ensureReadableOn(rawValue, bgCardL)
      : rawValue;

    root.style.setProperty(varName, value);

    // Auto-generate RGB variants for color variables
    if (COLOR_VARS_WITH_RGB.includes(varName)) {
      try {
        const rgb = hexToRgb(value);
        root.style.setProperty(`${varName}-rgb`, `${rgb.r}, ${rgb.g}, ${rgb.b}`);
      } catch {
        // Fallback to a neutral gray if parsing fails
        root.style.setProperty(`${varName}-rgb`, '128, 128, 128');
      }
    }
  });
}

/**
 * Remove custom theme CSS variables from root element
 */
function removeCustomThemeVariables(root: HTMLElement) {
  CUSTOM_THEME_CSS_VARS.forEach((varName) => {
    root.style.removeProperty(varName);
  });
  // Also remove auto-generated RGB variables
  COLOR_VARS_WITH_RGB.forEach((varName) => {
    root.style.removeProperty(`${varName}-rgb`);
  });
}

/**
 * ThemeApplicator
 *
 * Invisible component that syncs theme state from Zustand to DOM.
 * Should be placed once near the root of the app.
 */
export function ThemeApplicator() {
  const isDarkMode = useGlobalStore((state) => state.isDarkMode);
  const designStyle = useGlobalStore((state) => state.designStyle);
  const activeCustomThemeId = useGlobalStore((state) => state.activeCustomThemeId);
  const customThemePalettes = useGlobalStore((state) => state.customThemePalettes);

  // Apply dark mode class
  useEffect(() => {
    const root = document.documentElement;

    if (isDarkMode) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [isDarkMode]);

  // Apply design style class or custom theme variables
  useEffect(() => {
    const root = document.documentElement;
    const isCustomTheme = designStyle.startsWith('custom-');

    // Remove all theme classes first
    THEME_CLASSES.forEach((cls) => {
      root.classList.remove(cls);
    });

    if (isCustomTheme && activeCustomThemeId) {
      // Custom theme: apply inline CSS variables
      const palette = customThemePalettes[activeCustomThemeId];
      if (palette) {
        const activePalette = isDarkMode ? palette.dark : palette.light;
        applyCustomThemeVariables(root, activePalette);
      }
    } else {
      // Built-in theme: remove inline styles and add class
      removeCustomThemeVariables(root);
      const themeClass = getThemeClassName(designStyle as BuiltInDesignStyle);
      if (themeClass) {
        root.classList.add(themeClass);
      }
    }
  }, [designStyle, activeCustomThemeId, customThemePalettes, isDarkMode]);

  // --accent-text(accent 배경 위 글자색)는 **CSS 가 계산한다** — app.css 의
  //   lch(from var(--accent-color) calc((49.44 - l) * infinity) 0 0)
  // 여기서 JS 로 계산하던 코드가 있었는데 두 가지가 고장나 있었다:
  //  1) `--accent-color-rgb` 가 있어야 동작하는데 **내장 테마엔 그 변수가 없다**(커스텀 테마 전용)
  //     → 조용히 스킵되어 --accent-text 가 흰색에 굳었다. 노란 버튼(brutal 1.3:1)·주황 버튼
  //     (jeju 2.2:1)에 흰 글씨가 박혀 안 읽혔다.
  //  2) 인라인 style 주입이라 CSS 를 이겨, CSS 쪽 계산을 무력화했다.
  // CSS 의 lch() 는 내장·커스텀·테마빌더의 임의 색까지 전부 커버하므로 JS 계산은 제거한다.
  //
  // 남은 inline 잔재가 있으면 걷어낸다(같은 세션에서 옛 코드가 심어둔 값 방지).
  useEffect(() => {
    document.documentElement.style.removeProperty("--accent-text");
  }, [designStyle, activeCustomThemeId, customThemePalettes, isDarkMode]);

  // This component doesn't render anything
  return null;
}

export default ThemeApplicator;
